import { Link } from "react-router-dom";
import { ShoppingCart, FileSignature, Receipt, Send } from "lucide-react";
import { useTranslation } from "react-i18next";
import { formatPrice } from "@ckgo2/shared/utils";
import { formatDate } from "@/lib/format";
import type { OrderStatus } from "@ckgo2/shared/types";
import { ORDER_STATUS_CONFIG } from "@/lib/statusConfig";
import type { Customer, Order } from "./types";

interface CustomerOrdersPanelProps {
  customer: Customer;
  onDownloadDocument: (docType: "contract" | "invoice", order: Order) => void;
  onOpenSendModal: (type: "email" | "pdf", order?: Order) => void;
  onSelectOrder: (order: Order) => void;
}

export default function CustomerOrdersPanel({
  customer,
  onDownloadDocument,
  onOpenSendModal,
  onSelectOrder,
}: CustomerOrdersPanelProps) {
  const { t } = useTranslation();
  return (
    <div className="lg:col-span-2">
      <div className="card">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t("crm.customerOrders.title")}
          </h2>
          <Link to="/orders/new" className="btn-primary btn-sm">
            {t("crm.customerOrders.newOrder")}
          </Link>
        </div>

        {customer.orders && customer.orders.length > 0 ? (
          <div className="space-y-3">
            {customer.orders.map((order) => {
              const statusConfig =
                ORDER_STATUS_CONFIG[order.status as OrderStatus] || ORDER_STATUS_CONFIG.inquiry;
              return (
                <div
                  key={order.id}
                  className="rounded-lg border border-gray-200 p-4 dark:border-gray-700"
                >
                  <div className="flex items-center justify-between">
                    <Link
                      to={`/orders/${order.id}`}
                      className="hover:text-primary-600 flex-1 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <span className="font-medium text-gray-900 dark:text-white">
                          {order.orderNumber}
                        </span>
                        <span className={`badge text-xs ${statusConfig.className}`}>
                          {statusConfig.label}
                        </span>
                      </div>
                      <p className="mt-1 text-sm text-gray-500">
                        {order.trip?.title || t("crm.customerOrders.unknownTrip")}
                        {order.tripDate && (
                          <span className="ml-2">({formatDate(order.tripDate.startDate)})</span>
                        )}
                      </p>
                      <p className="text-xs text-gray-400">
                        {t("crm.customerOrders.createdAt", { date: formatDate(order.createdAt) })}
                      </p>
                    </Link>
                    <div className="text-right">
                      <p className="font-semibold text-gray-900 dark:text-white">
                        {formatPrice(order.totalPrice || 0)}
                      </p>
                    </div>
                  </div>

                  {/* Document download buttons */}
                  <div className="mt-3 flex items-center gap-2 border-t border-gray-100 pt-3 dark:border-gray-700">
                    <button
                      onClick={() => onDownloadDocument("contract", order)}
                      className="btn-ghost btn-sm text-xs"
                      title={t("crm.customerOrders.downloadContract")}
                    >
                      <FileSignature className="mr-1.5 h-4 w-4" />
                      {t("crm.customerOrders.contract")}
                    </button>
                    <button
                      onClick={() => onDownloadDocument("invoice", order)}
                      className="btn-ghost btn-sm text-xs"
                      title={t("crm.customerOrders.downloadInvoice")}
                    >
                      <Receipt className="mr-1.5 h-4 w-4" />
                      {t("crm.customerOrders.invoice")}
                    </button>
                    <button
                      onClick={() => {
                        onSelectOrder(order);
                        onOpenSendModal("email");
                      }}
                      className="btn-ghost btn-sm text-xs"
                      title={t("crm.customerDetail.sendEmail")}
                    >
                      <Send className="mr-1.5 h-4 w-4" />
                      {t("crm.customerOrders.email")}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex h-32 flex-col items-center justify-center text-gray-500">
            <ShoppingCart className="mb-2 h-8 w-8 text-gray-300" />
            <p>{t("crm.customerOrders.empty")}</p>
          </div>
        )}

        {/* Timeline */}
        {customer.firstTripDate && (
          <div className="mt-6 border-t pt-6 dark:border-gray-700">
            <h3 className="mb-4 font-medium text-gray-900 dark:text-white">
              {t("crm.customerOrders.timeline")}
            </h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-3">
                <div className="bg-success-500 h-2 w-2 rounded-full" />
                <span className="text-gray-600 dark:text-gray-400">
                  {t("crm.customerOrders.firstTrip", { date: formatDate(customer.firstTripDate) })}
                </span>
              </div>
              {customer.lastTripDate && customer.lastTripDate !== customer.firstTripDate && (
                <div className="flex items-center gap-3">
                  <div className="bg-primary-500 h-2 w-2 rounded-full" />
                  <span className="text-gray-600 dark:text-gray-400">
                    {t("crm.customerOrders.lastTrip", { date: formatDate(customer.lastTripDate) })}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
